import { t } from 'i18next';
import { NewOption } from './types';
import { ModoSobDemandaData } from './components/modoSobDemanda';
import { ModoEcoData } from './components/modoEco';
import { ControlModeDut4, ScheduleDut4 } from '~/providers/types/api-private';

export const defaultActionModeOptions: NewOption[] = [
  { label: t('refrigerar'), value: 'COOL' },
  { label: t('ventilar'), value: 'FAN' },
  { label: t('eco'), value: 'ECO' },
];

export const defaultModeOptions: NewOption[] = [
  { label: t('refrigerar'), value: 'COOL' },
  { label: t('ventilar'), value: 'FAN' },
  { label: t('modoDesligar'), value: 'OFF' },
];

export const comportamentoInicialProgHorarioOptions: NewOption[] = [
  { label: t('ventilar'), value: 'FAN' },
  { label: t('desligar'), value: 'OFF' },
  { label: t('semAcao'), value: 'NO_ACTION' },
];

export const ltiModeEcoOptions: NewOption[] = [
  { label: t('modoDesligar'), value: 'OFF' },
  { label: t('ventilar'), value: 'FAN' },
  { label: t('semAcao'), value: 'NO_ACTION' },
];

export const defaultActionPostModeOptions: NewOption[] = [
  { label: t('refrigerar'), value: 'COOL' },
  { label: t('ventilar'), value: 'FAN' },
  { label: t('modoDesligar'), value: 'OFF' },
  { label: t('semAcao'), value: 'NO_ACTION' },
];

export function labelFromModeValue(value: string | null | undefined, options: NewOption[]): string {
  if (!value) return '';
  return (options.find((opt) => opt.value === value)?.label as string) ?? value;
}

export function validateSchedule(type: 'modoSobDemanda' | 'eco' | '', data: ModoEcoData | ModoSobDemandaData): string | null {
  if (type === 'eco') return validateEcoSchedule(data as ModoEcoData);
  if (type === 'modoSobDemanda') return validateOnDemandSchedule(data as ModoSobDemandaData);
  return null;
}

function validateEcoSchedule(data: ModoEcoData) {
  if (!data.setpoint || Number.isNaN(Number(data.setpoint))) {
    return t('erroSetpointEcoObrigatorio');
  }

  if (!data.lti_cmd) {
    return t('erroCamposLtcLtiObrigatorios');
  }

  if (!data.ltc_degree || Number.isNaN(Number(data.ltc_degree))) {
    return t('erroTemperaturaLtcObrigatoriaRefrigeramento');
  }

  if (Number(data.ltc_degree) < 0 || Number(data.ltc_degree) > 30) {
    return t('valorEntre', { min: '0', max: '30' });
  }

  if (Number(data.ltc_degree) < Number(data.setpoint)) {
    return t('erroLTCdeveSerMaiorSetpoint', { ltc: data.ltc_degree, setpoint: data.setpoint });
  }

  if ((data.lti_cmd === 'FAN' || data.lti_cmd === 'OFF') && (!data.lti_degree || Number.isNaN(Number(data.lti_degree)))) {
    return t('erroTemperaturaLtiObrigatoriaRefrigeramento');
  }

  if ((data.lti_cmd === 'FAN' || data.lti_cmd === 'OFF') && data.lti_degree && Number(data.lti_degree) > Number(data.setpoint)) {
    return t('erroLTIdeveSerMenorSetpoint', { lti: data.lti_degree, setpoint: data.setpoint });
  }

  if (data.upperHysteresis) {
    const num = Number(data.upperHysteresis);
    if (Number.isNaN(num) || num < 0 || num > 5) return t('erroHistereseSuperiorInvalida');
  }

  if (data.lowerHysteresis) {
    const num = Number(data.lowerHysteresis);
    if (Number.isNaN(num) || num < 0 || num > 5) return t('erroHistereseInferiorInvalida');
  }

  return null;
}

function validateOnDemandSchedule(data: ModoSobDemandaData) {
  if (!data.driveTemperature || Number.isNaN(Number(data.driveTemperature))) {
    return t('erroTemperaturaAcionamentoObrigatoria');
  }

  if (Number(data.driveTemperature) < 0 || Number(data.driveTemperature) > 30) {
    return t('valorEntre', { min: '0', max: '30' });
  }

  if (!data.actionMode) {
    return t('erroModoAcaoObrigatorio');
  }

  if (data.actionMode === 'COOL' && (!data.setpoint || Number.isNaN(Number(data.setpoint)))) {
    return t('erroSetpointObrigatorioModoRefrigeramento');
  }

  if (data.actionPostMode === 'COOL' && (!data.actionPostSetpoint || Number.isNaN(Number(data.actionPostSetpoint)))) {
    return t('erroSetpointObrigatorioModoRefrigeramento');
  }

  if ((data.lti_cmd === 'FAN' || data.lti_cmd === 'OFF') && (!data.lti_degree || Number.isNaN(Number(data.lti_degree)))) {
    return t('erroTemperaturaLtiObrigatoriaRefrigeramento');
  }

  if ((data.lti_cmd === 'FAN' || data.lti_cmd === 'OFF') && data.lti_degree && data.setpoint && (data.actionMode === 'COOL' || data.actionMode === 'ECO') && Number(data.lti_degree) > Number(data.setpoint)) {
    return t('erroLTIdeveSerMenorSetpoint', { lti: data.lti_degree, setpoint: data.setpoint });
  }

  if (data.upperHysteresis) {
    const num = Number(data.upperHysteresis);
    if (Number.isNaN(num) || num < 0 || num > 5) return t('erroHistereseSuperiorInvalida');
  }

  if (data.lowerHysteresis) {
    const num = Number(data.lowerHysteresis);
    if (Number.isNaN(num) || num < 0 || num > 5) return t('erroHistereseInferiorInvalida');
  }

  return null;
}

function toActionCmd(mode: string | null, setpoint: string): string | undefined {
  if (mode === 'FAN') return 'FAN';
  if (mode === 'COOL' || mode === 'ECO' || (setpoint && mode == null)) return `SET${setpoint}`;
  return undefined;
}

export function setpointFromActionCmd(cmd: string | undefined): string {
  if (cmd?.startsWith('SET')) return cmd.slice(3); // Remove o prefixo 'SET' para obter o setpoint
  return '';
}

function toPostActionCmd(mode: string | null, setpoint: string): string | undefined {
  if (mode === 'FAN') return 'FAN';
  if (mode === 'COOL') return `SET${setpoint}`;
  if (mode === 'NO_ACTION') return 'NO_ACTION';
  if (mode === 'OFF') return 'OFF';
  return undefined;
}

export function actionPostModeFromCmd(cmd: string | undefined): string {
  if (cmd === 'FAN') return 'FAN';
  if (cmd?.startsWith('SET')) return 'COOL';
  if (cmd === 'NO_ACTION') return 'NO_ACTION';
  if (cmd === 'OFF') return 'OFF';
  return '';
}

function ecoScheduleData(data: ModoEcoData) {
  return {
    default_cmd: data.initialBehaviorProgrammingHour ? data.initialBehaviorProgrammingHour : undefined,
    hist_sup: data.upperHysteresis ? Number(data.upperHysteresis) : undefined,
    hist_inf: data.lowerHysteresis ? Number(data.lowerHysteresis) : undefined,
    ltc_degree: data.ltc_degree ? Number(data.ltc_degree) : undefined,
    setpoint: data.setpoint ? Number(data.setpoint) : undefined,
    sp_cmd: toActionCmd(null, data.setpoint),
    lti_cmd: data.lti_cmd || undefined,
    lti_degree: data.lti_degree ? Number(data.lti_degree) : undefined,
    sp_action: undefined,
    sp_end_cmd: undefined,
    sp_intv: undefined,
  };
}

function onDemandScheduleData(data: ModoSobDemandaData) {
  return {
    default_cmd: undefined,
    temp_trigger: data.driveTemperature ? Number(data.driveTemperature) : undefined,
    sp_action: data.actionMode ?? undefined,
    sp_cmd: toActionCmd(data.actionMode, data.setpoint),
    sp_end_cmd: toPostActionCmd(data.actionPostMode, data.actionPostSetpoint),
    lti_degree: data.lti_degree ? Number(data.lti_degree) : undefined,
    hist_sup: data.upperHysteresis ? Number(data.upperHysteresis) : undefined,
    hist_inf: data.lowerHysteresis ? Number(data.lowerHysteresis) : undefined,
    sp_intv: data.actionTime ? Number(data.actionTime) : undefined,
    ltc_degree: undefined,
    lti_cmd: data.lti_cmd || undefined,
  };
}

function scheduleData(
  type: 'modoSobDemanda' | 'eco',
  data: ModoSobDemandaData | ModoEcoData,
) {
  if (type === 'eco') return ecoScheduleData(data as ModoEcoData);
  if (type === 'modoSobDemanda') return onDemandScheduleData(data as ModoSobDemandaData);
  return null;
}

export function parseScheduleData(
  title: string,
  schedule_status: boolean,
  ctrloper: ControlModeDut4 | null,
  days: {
    mon: boolean;
    tue: boolean;
    wed: boolean;
    thu: boolean;
    fri: boolean;
    sat: boolean;
    sun: boolean;
  }, initial: string, final: string,
  data: ModoSobDemandaData | ModoEcoData,
  setpoint?: string | number,
  dutScheduleId?: number | null,
): ScheduleDut4 {
  const effectiveType = ctrloper === '3_SOB_DEMANDA' ? 'modoSobDemanda'
    : ctrloper === '4_ECO' ? 'eco'
      : null;
  const scheduleDataInfos = effectiveType != null ? scheduleData(effectiveType, data) : null;
  const ecoInfos = effectiveType === 'eco' ? scheduleDataInfos as ReturnType<typeof ecoScheduleData> : null;
  const onDemandInfos = effectiveType === 'modoSobDemanda' ? scheduleDataInfos as ReturnType<typeof onDemandScheduleData> : null;

  const baseData: ScheduleDut4 = {
    ...(dutScheduleId != null ? { DUT_SCHEDULE_ID: dutScheduleId } : {}),
    SCHEDULE_TITLE: title, // AUTOMATIONS_PERIODS
    SCHEDULE_STATUS: schedule_status, // AUTOMATIONS_PERIODS
    BEGIN_TIME: initial, // AUTOMATIONS_PERIODS
    END_TIME: final, // AUTOMATIONS_PERIODS
    DAYS: days, // SCHEDULES
    CTRLOPER: ctrloper,
    LTC: scheduleDataInfos?.ltc_degree != null ? scheduleDataInfos.ltc_degree : undefined,
    LTI: scheduleDataInfos?.lti_degree != null ? scheduleDataInfos.lti_degree : undefined,
    LTI_CMD: scheduleDataInfos?.lti_cmd,
    SP_ACTION: scheduleDataInfos?.sp_action,
    SP_INTV: scheduleDataInfos?.sp_intv,
    SP_CMD: scheduleDataInfos?.sp_cmd,
    SP_END_CMD: scheduleDataInfos?.sp_end_cmd,
    UPPER_HYSTERESIS: scheduleDataInfos?.hist_sup != null ? scheduleDataInfos.hist_sup : undefined,
    LOWER_HYSTERESIS: scheduleDataInfos?.hist_inf != null ? scheduleDataInfos.hist_inf : undefined,
    SETPOINT: effectiveType === 'modoSobDemanda'
      ? undefined
      : (ecoInfos?.setpoint != null ? ecoInfos.setpoint : (setpoint != null ? Number(setpoint) : undefined)),
    TEMP_TRIGGER: onDemandInfos?.temp_trigger != null ? onDemandInfos.temp_trigger : undefined,
    DEFAULT_COMMAND: scheduleDataInfos?.default_cmd,
  };

  return baseData;
}
